import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../../contexts/Auth'
import { selectPaddle, selectUser, setUser } from '../../redux/systemSlice'
import Button from '../Buttons'
import Premium from '../Premium/Premium'
import './_Modal.scss'
import { isValidEmail } from '../../helpers/utility'
import { useToken } from '../../hooks/token'
import * as Sentry from '@sentry/react'
import './_Account.scss'
import ReauthModal from './Reauth'
import Modal from './Modal'
import LoginModal from './Login'
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react'
import useIsPremium from '../../hooks/useIsPremium'
import { FEATURE_FLAGS, useAnnualPlan } from '../../hooks/featureFlags'
import ReferralModal from '../Referral'
import Input from '../Input'
import { LuCircleDollarSign, LuCrown, LuInfinity, LuPencil, LuShield } from 'react-icons/lu'
import TrialUpgrade from './TrialUpgrade'
import useTrialEssayCredits from '../../hooks/useTrialEssayCredits'

const errorToText = {
	'email-not-changed': 'To change your account email, please enter a different email than your current email.',
	'auth/email-already-in-use': 'This email is already used by another account, please try again with a new email',
	'invalid-email': 'Please enter a valid email',
	unknown: 'Unknown error, please try again.',
}

function AccountModal({ errorProp, isOpen, closeModal }: { errorProp?: any; isOpen: boolean; closeModal: () => void }) {
	const { logout, currentUser } = useAuth() as any
	const user = useSelector(selectUser)
	const [emailInput, setEmailInput] = useState(user ? user.email : '')
	const [manageSubscriptionOpen, setManageSubscriptionOpen] = useState(false)
	const userToken = useToken()
	const dispatch = useDispatch()
	const [error, setError] = useState<
		undefined | 'email-not-changed' | 'auth/email-already-in-use' | 'invalid-email' | 'unknown'
	>(undefined)
	const [reauthModalOpen, setReauthModalOpen] = useState(false)
	const [referralModalOpen, setReferralModalOpen] = useState(false)
	const [loginModalOpen, setLoginModalOpen] = useState(false)
	const [annualPlan] = useAnnualPlan()
	const hasPremiumAccess = useIsPremium()
	const paddle = useSelector(selectPaddle)
	const posthog = usePostHog()
	const usePaddle = useFeatureFlagVariantKey(FEATURE_FLAGS.PADDLE)
	const deemphasizeCancelFF = useFeatureFlagVariantKey(FEATURE_FLAGS.DEEMPHASIZE_CANCEL)
	const isSignedInWithGoogle = currentUser?.providerData.some((provider: any) => provider.providerId === 'google.com')
	const [trialUpgradeModalOpen, setTrialUpgradeModalOpen] = useState(false)
	const { essaysRemaining, essayLimit } = useTrialEssayCredits()

	useEffect(() => {
		if (user?.email) {
			setEmailInput(user ? user.email : '')
		}
	}, [user?.email, currentUser])

	useEffect(() => {
		setError(errorProp)
		//Update only on initial page load
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	async function logoutUser() {
		closeModal()
		await logout()
		window.location.reload()
	}

	async function openReauth() {
		const currentEmail = currentUser?.email ?? ''

		if (emailInput === currentEmail) {
			setError('email-not-changed')
			return
		}

		if (!isValidEmail(emailInput)) {
			setError('invalid-email')
			return
		}

		setReauthModalOpen(true)
	}

	async function disableHumanizer() {
		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
			},
			body: JSON.stringify({
				userId: user.id,
			}),
		}
		fetch(process['env']['REACT_APP_API_ROOT'] + '/payments/delete-humanizer/', requestOptions)
			.then(() => {
				dispatch(setUser({ ...user, hasPaidAddon: false }))
			})
			.catch((err) => Sentry.captureException(err))
	}

	function createPortalSession() {
		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: currentUser ? `Bearer ${userToken}` : '',
			},
			body: JSON.stringify({
				userId: user.id,
			}),
		}
		fetch(process['env']['REACT_APP_API_ROOT'] + '/payments/portal-session/', requestOptions)
			.then((res) => res.json())
			.then((response: { url: string }) => {
				if (response.url) {
					window.open(response.url, '_self')
				}
			})
			.catch((err) => Sentry.captureException(err))
	}

	const cancelSubscription = async () => {
		posthog.capture('cancel-subscription')
		if (usePaddle === 'test' && paddle && user.paddlePlanId) {
			paddle.Retain.initCancellationFlow({
				subscriptionId: user.paddlePlanId,
			}).then((result: any) => {
				if (result.status === 'chose_to_cancel') {
					window.location.reload()
				}
			})
			return
		}
		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: currentUser ? `Bearer ${userToken}` : '',
			},
			body: JSON.stringify({
				customerId: user.stripeId,
			}),
		}
		fetch(process['env']['REACT_APP_API_ROOT'] + '/payments/get-hmac-hash/', requestOptions)
			.then((res) => res.json())
			.then((response: { hash: string }) => {
				if (response.hash) {
					closeModal()
					// @ts-ignore
					window.churnkey.init('show', {
						customerId: user.stripeId,
						authHash: response.hash,
						appId: '5iixefaqw',
						mode: 'live',
						provider: 'stripe',
					})
				}
			})
			.catch((err) => Sentry.captureException(err))
	}

	if (reauthModalOpen) {
		return (
			<ReauthModal
				newEmail={emailInput?.toLowerCase()}
				closeModal={() => setReauthModalOpen(false)}
				isOpen={reauthModalOpen}
				setError={setError}
			/>
		)
	}

	if (referralModalOpen) {
		return <ReferralModal setModalOpen={setReferralModalOpen} open={referralModalOpen} />
	}

	return (
		<>
			<LoginModal
				premiumPath={true}
				open={loginModalOpen}
				setModalOpen={(open: boolean) => {
					setLoginModalOpen(false)
				}}
				annualPlan={annualPlan}
				context="account"
			/>
			<TrialUpgrade
				open={trialUpgradeModalOpen}
				setModalOpen={setTrialUpgradeModalOpen}
				openForPaymentRetries={true}
				outOfCredits={true}
				openForUndetectable={false}
			/>
			<Modal portalClassName="account" open={isOpen} closeModal={closeModal}>
				<>
					<h5 className="header-md">Your Account</h5>
					<div className="flex flex-wrap md:flex-nowrap justify-between md:pt-[30px] md:gap-0 gap-4">
						<div className="w-full pr-0 text-center pt-3 md:w-1/2 md:pt-0">
							<Input
								placeholder="me@example.com"
								onChange={(e) => {
									setEmailInput(e.target.value)
									setError(undefined)
								}}
								value={emailInput}
							/>

							{!isSignedInWithGoogle && (
								<Button className="my-[15px] w-full" type="outline" onClick={openReauth}>
									<span>Update email</span>
								</Button>
							)}
							<Button className="w-full mt-3" type="ghost" onClick={logoutUser}>
								Log out
							</Button>
							{error && <div className="highlighted error">{errorToText[error]}</div>}
						</div>
						<div className="w-full pl-0 pb-[30px] min-h-[200px] md:pl-2 md:w-[calc(50%-21px)] md:border-l border-t md:border-t-0 border-gray-300 md:border-b-0 pt-4 md:pt-0">
							{!hasPremiumAccess && (
								<Premium
									closeParentModal={closeModal}
									setLoginModal={(open: boolean) => {
										setLoginModalOpen(open)
									}}
									shorten
								/>
							)}
							{hasPremiumAccess && user.isOnFreeTrial && (
								<div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
									<div className="flex flex-col items-center gap-2">
										<div className="text-xl font-bold">You're on a Free Trial</div>
										<div className="flex items-center gap-2 mt-1">
											{/* <div className="flex gap-1">
											{Array.from({ length: essayLimit }).map((_, i) => (
												<div
													key={i}
													className={`w-3 h-3 rounded-full ${
														i < essaysRemaining ? 'bg-amber-500' : 'bg-gray-200'
													}`}
												/>
											))}
										</div> */}
											<span
												className={`text-sm font-semibold ${essaysRemaining <= 1 ? 'text-red-600' : 'text-gray-700'}`}
											>
												{essaysRemaining} of {essayLimit} essays left
											</span>
										</div>
										<p className="text-xs text-gray-500 text-center mt-1">
											Upgrade to Premium for unlimited essays, AI humanization, and more.
										</p>
										<Button
											type="default"
											className="w-full mt-2 !bg-indigo-600 hover:!bg-indigo-700 text-white font-bold py-2.5 rounded-xl"
											onClick={() => {
												closeModal()
												setTrialUpgradeModalOpen(true)
											}}
										>
											<span>Upgrade Now</span>
										</Button>
										<div className="pt-2 w-full mt-2 border-t border-gray-300">
											<div
												onClick={() => setManageSubscriptionOpen(!manageSubscriptionOpen)}
												className="text-left text-gray-700 text-xs cursor-pointer hover:text-gray-800 transition-colors w-full"
											>
												Need to make changes?
											</div>
											{manageSubscriptionOpen && (
												<div className="flex flex-col gap-2 mt-3 w-full text-left">
													<div
														onClick={createPortalSession}
														className="text-sm text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
													>
														Manage billing
													</div>
													<div
														onClick={cancelSubscription}
														className="text-sm text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
													>
														Manage subscription
													</div>
												</div>
											)}
										</div>
									</div>
								</div>
							)}
							{hasPremiumAccess && !user.isOnFreeTrial && (
								<>
									{deemphasizeCancelFF === 'test' ? (
										<div className="bg-link/10 rounded-xl p-4">
											<div className="flex flex-col items-center gap-2">
												<div className="bg-link rounded-xl flex items-center gap-1 text-white px-3 py-0.5 text-xs uppercase font-semibold">
													{LuCrown({ className: 'text-white' })}
													<div>Premium</div>
												</div>
												<div className="text-xl font-bold">You're on the Premium plan</div>
												<div className="flex flex-col gap-2 w-full mt-3">
													{[
														{ name: 'Unlimited Essays', icon: LuInfinity },
														{ name: 'AI Humanizer and Detector', icon: LuShield },
														{ name: 'Personalized Writing Style', icon: LuPencil },
													].map((item) => (
														<div className="flex items-center gap-2 w-full">
															{item.icon({ className: 'text-link text-base mt-1 shrink-0' })}
															<div className="text-gray-900 font-semibold text-[15px]">{item.name}</div>
														</div>
													))}
												</div>
												<div className="pt-2 w-full mt-2 border-t border-gray-300">
													<div
														onClick={() => setManageSubscriptionOpen(!manageSubscriptionOpen)}
														className="text-left text-gray-700 text-xs cursor-pointer hover:text-gray-800 transition-colors w-full"
													>
														Need to make changes?
													</div>
													{manageSubscriptionOpen && (
														<div className="flex flex-col gap-2 mt-3 w-full text-left">
															<div
																onClick={createPortalSession}
																className="text-sm text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
															>
																Manage billing
															</div>
															<div
																onClick={cancelSubscription}
																className="text-sm text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
															>
																Manage subscription
															</div>
														</div>
													)}
												</div>
											</div>
										</div>
									) : (
										<>
											<div className="text-xl md:text-2xl font-bold">
												You're on the <span className="highlighted">Premium</span> plan
											</div>
											{user?.subscriptionStatus && <div className="subscriptionStatus">{user.subscriptionStatus}</div>}
											{user?.paddlePlanStatus === 'scheduled_cancellation' && usePaddle === 'test' && (
												<div className="subscriptionStatus">Your subscription is scheduled for cancellation</div>
											)}
											{!(usePaddle === 'test') && (
												<Button type="outline" className="managePlan mt-6 w-full" onClick={createPortalSession}>
													<span>Manage Billing</span>
												</Button>
											)}
											{(user?.paddlePlanStatus === 'active' || !user.subscriptionStatus) && (
												<Button
													type="outline"
													className="managePlan mt-6 text-error-default w-full"
													onClick={cancelSubscription}
												>
													<span className="text-error-dark">Cancel</span>
												</Button>
											)}
										</>
									)}
								</>
							)}
						</div>
					</div>
					{user.hasPaidAddon && (
						<>
							<div className="dividingBorder" />
							<div className="humanizerUpgrade">
								<div className="descriptionSection">
									<div>AI Humanizer and Detector enabled.</div>
									<div className="providedBy">
										Powered by <a href="https://mask.ai/">Ghost AI</a>
									</div>
								</div>
								<Button type="secondary" htmlType="submit" onClick={disableHumanizer}>
									<span>Disable</span>
								</Button>
							</div>
						</>
					)}
				</>
			</Modal>
		</>
	)
}

export default AccountModal
